import Button from '@repo/ui/button'
import { DocumentIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { getMedicalStatement } from '../../../api/enrollments/get-medical-statement'
import {
  resendMedicalStatement,
  sendMedicalStatement
} from '../../../api/enrollments/send-medical-statment'
import { validateMedicalStatement } from '../../../api/enrollments/validate-medical-statement'
import useAuth from '../../../lib/hooks/use-auth'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { MedicalStatusEnum } from '../../../lib/types/enums/enrollment/medical-status.enum'
import { MedicalStatementProperties } from '../../../lib/types/models/enrollment/enrollment-medical-statement.properties'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import ResendMedicalStatementForm from './enrollment-form/resend-medical-statment-form'
import SendMedicalStatementForm from './enrollment-form/send-medical-statment-form'
import ValidateMedicalStatementForm from './enrollment-form/validate-medical-statement-form'

type Props = {
  enrollmentId: number
  medicalStatus?: MedicalStatusEnum
  /** Fallback content for the validation modal when the attestation carries none yet. */
  additionalMedicalInformations?: string | null
  /** Pre-fills the send modal's translation, next to the additional information. */
  additionalMedicalInformationsEn?: string | null
  /** Called after any attestation action succeeded, so the host page can refresh its own data. */
  onSuccess?: () => void
}

/**
 * Send, resend and validate the medical attestation. Shared by the enrollment form and the
 * enrollment profile so a confirmed enrollment gets the same actions as an unconfirmed one.
 */
const MedicalStatementActions = ({
  enrollmentId,
  medicalStatus,
  additionalMedicalInformations,
  additionalMedicalInformationsEn,
  onSuccess
}: Props) => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { modalState, toggleModal, closeModel } = useModalManager<
    'send-medical-statement' | 'resend-medical-statement' | 'validate-medical-statement'
  >(['send-medical-statement', 'resend-medical-statement', 'validate-medical-statement'])

  const { data: medicalAttestation } = useQuery({
    queryKey: ['medical-attestation', enrollmentId],
    queryFn: () => getMedicalStatement(enrollmentId)
  })

  const onActionSuccess = (modal: Parameters<typeof closeModel>[0]) => {
    queryClient.invalidateQueries({ queryKey: ['medical-attestation', enrollmentId] })
    closeModel(modal)
    onSuccess?.()
  }

  const { mutateAsync: sendMedicalStatementMutation } = useMutation({
    mutationFn: sendMedicalStatement,
    onError: showAxiosError,
    onSuccess: () => onActionSuccess('send-medical-statement')
  })

  const { mutateAsync: resendMedicalStatementMutation } = useMutation({
    mutationFn: resendMedicalStatement,
    onError: showAxiosError,
    onSuccess: () => onActionSuccess('resend-medical-statement')
  })

  const {
    mutateAsync: validateMedicalStatementMutation,
    isPending: isValidateMedicalStatementPending
  } = useMutation({
    mutationFn: validateMedicalStatement,
    onError: showAxiosError,
    onSuccess: () => onActionSuccess('validate-medical-statement')
  })

  const button = medicalStatus
    ? MedicalStatementProperties.getMedicalStatementButton(
        {
          onSend: () => toggleModal('send-medical-statement'),
          onResend: () => toggleModal('resend-medical-statement'),
          onValidate: () => toggleModal('validate-medical-statement')
        },
        medicalAttestation ?? undefined,
        user.isMasterLec,
        medicalStatus
      )
    : undefined

  // Without a medical status the button cannot be resolved: showing the send action by default would
  // offer to send an attestation for an enrollment that may not need one.
  if (!button) return null

  return (
    <>
      <Button
        type="button"
        label={button.label}
        variant="outline"
        icon={button.icon}
        loading={isValidateMedicalStatementPending}
        className="border-grey-light"
        onClick={button.onClick}
      />

      <Modal
        hideActions
        size="medium"
        icon={DocumentIcon}
        isOpen={modalState['send-medical-statement']}
        title="Envoi d'une attestation d'autonomie"
        onCancel={() => closeModel('send-medical-statement')}
      >
        <SendMedicalStatementForm
          enrollmentId={enrollmentId}
          defaultValues={{
            content: additionalMedicalInformations ?? undefined,
            contentEn: additionalMedicalInformationsEn ?? undefined
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('send-medical-statement'),
            onConfirm: (data) => sendMedicalStatementMutation({ id: enrollmentId, ...data })
          })}
        />
      </Modal>

      <Modal
        hideActions
        size="medium"
        icon={DocumentIcon}
        isOpen={modalState['resend-medical-statement']}
        title="Renvoyer l'attestation"
        onCancel={() => closeModel('resend-medical-statement')}
      >
        <ResendMedicalStatementForm
          enrollmentId={enrollmentId}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('resend-medical-statement'),
            onConfirm: (data) => resendMedicalStatementMutation({ id: enrollmentId, ...data })
          })}
        />
      </Modal>

      <Modal
        hideActions
        size="medium"
        icon={DocumentIcon}
        isOpen={modalState['validate-medical-statement']}
        title="Valider l'envoi de l'attestation"
        onCancel={() => closeModel('validate-medical-statement')}
      >
        <ValidateMedicalStatementForm
          defaultValues={{
            type: medicalAttestation?.type,
            content: medicalAttestation?.content ?? additionalMedicalInformations ?? undefined
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('validate-medical-statement'),
            onConfirm: (data) => validateMedicalStatementMutation({ id: enrollmentId, ...data }),
            isLoading: isValidateMedicalStatementPending
          })}
        />
      </Modal>
    </>
  )
}

export default MedicalStatementActions
